"use client";
import React from "react";
import { Box } from "@dxp-feed/ui/Box";
import { Text } from "@dxp-feed/ui/Text";
import { addDataLayer } from "@/helpers/dataLayer";
import { sendSalesforceInteraction } from "@/helpers/cdnSdk";
import { Button } from "@dxp-feed/ui/Button";
import { getUTMParameter } from "@/helpers/getUTMParameters";
import { useAppSelector, useEffect } from "@/utils/hooks/reactUseCustomHooks";
import AdSlot from "@/helpers/gpt";
import { adsScript } from "@/utils/adsTempConstant";
import Link from "next/link";
import ImageWrapper from "@/components/ImageWrapper";
import { stripHtmlTags } from "@/helpers/useCommonFunctions";
import { SiteContainer } from "@/components/Container";
 
interface ILatestCatchUp {
    columnControl: any;
}
 
const renderSceneTile = (data: any, Uid: string, className: string) => (
    <Box
        as="div"
        className={`recipes-featured-block scene-featured-dark-card relative bg-brandBlack md:pt-[50px] md:px-[53px] lg:px-[60px] lg:pb-[90px] pt-8 pb-[42px] xs:px-9 px-6 rounded-xl overflow-hidden flex items-start justify-between flex-col observable-tile h-full ${className}`}
        tabIndex={0}
        data-click-text="scene-card-logo-image"
        data-click-url="1"
        onClick={() => {
            addDataLayer({
                event: "tile_click",
                click_text: data?.sceneheading ? stripHtmlTags(data?.sceneheading) : "default",
                click_url: data?.link,
                page_title: document.title,
            });
            sendSalesforceInteraction({
                name: "homepage_clicks",
                banner: process.env.NEXT_PUBLIC_MERCHANT_IDENTIFIER,
                clickText: data?.sceneheading ? stripHtmlTags(data?.sceneheading) : "default",
                clickUrl: `${process.env.NEXT_PUBLIC_BASE_URL}${data?.link ?? data?.linkURL}`,
                isAnonymous: Uid ? 0 : 1,
                gigyaUid: Uid,
                section: "tile",
                utmSource: getUTMParameter("utm_source"),
                utmMedium: getUTMParameter("utm_medium"),
                utmCampaign: getUTMParameter("utm_campaign"),
            });
        }}
    >
        <Box as="div" className="scene-card-logo-image">
            <ImageWrapper
                src={`${process.env.NEXT_PUBLIC_AEM_PATH_URL}${data?.sceneLogo}`}
                width={163}
                height={48}
                className="w-[100px] md:w-[163px]"
                alt="scene plus image"
            />
        </Box>
        <Box
            as="div"
            className="scene-logo-dots-img absolute xs:right-[48px] right-[28px] md:right-[80px] xs:top-[25%] top-[16%] xl:top-[12%] 2xl:top-[25%]"
        >
            <ImageWrapper
                src={`${process.env.NEXT_PUBLIC_AEM_PATH_URL}${data?.scenedotimg}`}
                width={81}
                height={81}
                className="w-[50px] md:w-[81px]"
                alt="scene plus image"
            />
        </Box>
        <Box
            as="div"
            className="recipes-featured-block-desc xs:pt-[78px] lg:pt-[110px] 2lg:pt-[58px] pt-[58px]"
        >
            <Text
                as="h3"
                className="text-brandWhite text-[32px] md:text-[48px] leading-none uppercase mb-[20px] md:mb-[35px] lg:mb-[32px] font-bold"
            >
                {data?.sceneheading
                    ? new DOMParser().parseFromString(
                        data?.sceneheading,
                        "text/html"
                    ).body.textContent
                    : ""}
            </Text>
            <Text
                as="p"
                className="text-brandWhite text-base font-normal leading-normal mb-[30px] md:mb-[53px] lg:mb-[48px] max-w-[220px] md:max-w-[400px] lg:max-w-[330px]"
            >
                {data?.sceneDes}
            </Text>
            <Button variant="outline" asChild>
                {data?.linkTitle && (
                    <Link
                        className="font-normal bg-brandWhite text-brandblack border-[1px]  hover:text-brandWhite border-brandWhite
                        before:bg-brandBlack  hover:border-brandWhite !text-body  px-[24px] focus:bg-brandWhite text-brandwhite focus:border-brandWhite"
                        href={data?.link || "#"}
                        target={data?.target}
                    >
                        {data?.linkTitle}
                    </Link>
                )}
            </Button>
        </Box>
        <Box
            as="div"
            className="bottom-right-fixed-img absolute right-0 bottom-0"
        >
            <ImageWrapper
                src={`${process.env.NEXT_PUBLIC_AEM_PATH_URL}${data?.sceneBucketImg}`}
                width={220}
                height={183}
                className="xs:w-[134px] md:w-[220px] w-[104px]"
                alt="scene plus image"
            />
        </Box>
    </Box>
);
 
const renderTeaser = (data: any, Uid: string, className: string, index: string) => (
    <Link href={data?.linkURL || "#"}>
        <Box
            as="div"
            className={`latest-offer-card w-full rounded-[12px] bg-orange50 observable-tile flex items-center ${className}`}
            data-click-text={data?.imageResource?.alt}
            data-click-url={index}
            onClick={() => {
                addDataLayer({
                    event: "tile_click",
                    click_text: data?.imageResource?.alt ? data?.imageResource?.alt : "default", 
                    click_url: index,
                    page_title: document.title,
                });
                sendSalesforceInteraction({
                    name: "homepage_clicks",
                    banner: process.env.NEXT_PUBLIC_MERCHANT_IDENTIFIER,
                    clickText: data?.imageResource?.alt ? data?.imageResource?.alt : "default",
                    clickUrl: `${process.env.NEXT_PUBLIC_BASE_URL}${data?.linkURL ? data?.linkURL : "default"}`,
                    isAnonymous: Uid ? 0 : 1,
                    gigyaUid: Uid,
                    section: "tile",
                    utmSource: getUTMParameter("utm_source"),
                    utmMedium: getUTMParameter("utm_medium"),
                    utmCampaign: getUTMParameter("utm_campaign"),
                });
            }}
        >
            <ImageWrapper
                src={`${process.env.NEXT_PUBLIC_AEM_PATH_URL}${data?.fileReference}`}
                width={423}
                height={138}
                className="rounded-xl w-full h-full"
                alt={data?.imageResource?.alt ?? ""}
            />
        </Box>
    </Link>
);
 
export default function LatestCatchUpTiles({ columnControl }: ILatestCatchUp) {
 
    const path = useAppSelector((state) => state?.adsSclice?.page ?? "");
    const { uid: Uid } = useAppSelector((state) => state.gigyaslice);
 
    const observeTile = (
        entries: IntersectionObserverEntry[],
        observer: IntersectionObserver
    ) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                const { dataset } = entry.target as HTMLElement;
 
                addDataLayer({
                    event: "tile_view",
                    click_text: dataset.clickText ? dataset.clickText : "default",
                    click_url: dataset.clickUrl,
                    page_title: document.title,
                });
 
                sendSalesforceInteraction({
                    name: "homepage_view",
                    banner: process.env.NEXT_PUBLIC_MERCHANT_IDENTIFIER,
                    text: dataset.clickText ? dataset.clickText : "default",
                    isAnonymous: Uid ? 0 : 1,
                    gigyaUid: Uid,
                    section: "tile",
                    utmSource: getUTMParameter("utm_source"),
                    utmMedium: getUTMParameter("utm_medium"),
                    utmCampaign: getUTMParameter("utm_campaign"),
                });
 
                observer.unobserve(entry.target);
            }
        });
    };
 
    useEffect(() => {
        const observer = new IntersectionObserver(observeTile, {
            threshold: 0.5,
        });
 
        const tiles = document.querySelectorAll(".observable-tile");
        tiles.forEach((tile) => observer.observe(tile));
 
        return () => {
            tiles.forEach((tile) => observer.unobserve(tile));
        };
    }, [Uid, columnControl]);
 
    if (!columnControl || !columnControl[":items"] || !columnControl[":itemsOrder"]) {
        return null;
    }
 
    const renderColumn = (columnKey: string) => {
        const columnData = columnControl[":items"][columnKey];
        if (!columnData || !columnData[":items"] || !columnData[":itemsOrder"]) {
            return null;
        }
 
        const columnClassNames = columnData["columnClassNames"] || {};
 
        const items = columnData[":items"];
        const itemsOrder = columnData[":itemsOrder"];
 
        return (
            <Box as="div" key={columnKey} className="xl:flex xl:flex-col xl:gap-6">
                {itemsOrder.map((itemKey: string, index: number) => {
                    const itemData = items[itemKey];
                    const className = columnClassNames[itemKey] || "";
 
                    if (itemKey === "columncontrol" && itemData[":items"] && itemData[":itemsOrder"]) {
                        // Handle nested column control
                        const nestedItems = itemData[":items"];
                        const nestedItemsOrder = itemData[":itemsOrder"];
                        const nestedColumnClassNames = itemData["columnClassNames"] || {};
 
                        return (
                            <AdSlot
                                Fallback={
                                    <Box
                                        as="div"
                                        key={`${columnKey}-${itemKey}`}
                                        className="grid grid-cols-2 gap-[8px] pt-[8px] xl:pt-[1px] md:gap-[16px] xl:gap-[24px]"
                                    >
                                        {nestedItemsOrder.map((nestedColumnKey: string, ind: number) => {
                                            const nestedColumnData = nestedItems[nestedColumnKey];
                                            if (!nestedColumnData || !nestedColumnData[":items"] || !nestedColumnData[":itemsOrder"]) {
                                                return null;
                                            }
 
                                            const nestedItemKey = nestedColumnData[":itemsOrder"][0];
                                            const nestedItemData = nestedColumnData[":items"][nestedItemKey];
                                            const nestedClassName = nestedColumnClassNames[nestedItemKey] || "";
 
                                            if (nestedItemKey === "teaser" || nestedItemKey === "teaser_587734492") {
                                                return (
                                                    <Link
                                                        key={`${columnKey}-${itemKey}-${nestedColumnKey}`}
                                                        href={nestedItemData?.linkURL || "#"}
                                                    >
                                                        <Box
                                                            as="div"
                                                            className={`latest-offer-tile observable-tile rounded-[12px] overflow-hidden xl:pt-0 2xl:pt-[4px] md:pt-[8px]${nestedClassName}`}
                                                            data-click-text={nestedItemData?.imageResource?.alt}
                                                            data-click-url={(ind + 2 + index).toString()}
                                                            onClick={() => {
                                                                addDataLayer({
                                                                    event: "tile_click",
                                                                    click_text: nestedItemData?.imageResource?.alt ? nestedItemData?.imageResource?.alt : "default",
                                                                    click_url: (ind + 2 + index).toString(),
                                                                    page_title: document.title,
                                                                });
                                                                sendSalesforceInteraction({
                                                                    name: "homepage_clicks",
                                                                    banner: process.env.NEXT_PUBLIC_MERCHANT_IDENTIFIER,
                                                                    clickText: nestedItemData?.imageResource?.alt ? nestedItemData?.imageResource?.alt : "default",
                                                                    clickUrl: `${process.env.NEXT_PUBLIC_BASE_URL}${nestedItemData?.linkURL ? nestedItemData?.linkURL : "default"}`,
                                                                    isAnonymous: Uid ? 0 : 1,
                                                                    gigyaUid: Uid ? 0 : 1,
                                                                    section: "tile",
                                                                    utmSource: getUTMParameter("utm_source"),
                                                                    utmMedium: getUTMParameter("utm_medium"),
                                                                    utmCampaign: getUTMParameter("utm_campaign"),
                                                                });
                                                            }}
                                                        >
                                                            <ImageWrapper
                                                                src={`${process.env.NEXT_PUBLIC_AEM_PATH_URL}${nestedItemData?.fileReference}`}
                                                                width={342}
                                                                height={285}
                                                                className="rounded-xl w-full h-full object-cover"
                                                                alt={nestedItemData?.imageResource?.alt ?? ""}
                                                            />
                                                        </Box>
                                                    </Link>
                                                );
                                            }
                                            return null;
                                        })}
                                    </Box>
                                }
                                id={adsScript?.[path]?.double_image?.id}
                                className={"overflow-hidden mt-[8px] md:mt-[16px] xl:mt-0"}
                            />
 
                        );
                    }
 
                    if (itemKey === "scenetile") {
                        return renderSceneTile(itemData, Uid as string, className);
                    }
 
                    if (itemKey === "teaser") {
                        return (
                            <Box
                                as="div"
                                key={`${columnKey}-${itemKey}`}
                                className="recipes-featured-cards-row grid gap-[8px] md:gap-[16px] lg:gap-[30px] mb-[0px] xl:mb-[0px]"
                            >
                                {renderTeaser(itemData, Uid as string, className, columnKey.split("-")[1])}
                            </Box>
                        );
                    }
                    return null;
                })}
            </Box>
        );
    };
 
    return (
        <SiteContainer className="!pl-6 2md:!pl-12 lg:!px-12 ContainerMain">
            <Box as="div" className="recipes-outer-section bg-brandWhite">
                <Box as="div" className="recipes-for-you-section pb-[0px] lg:pb-[0px]">
                    <Box
                        as="div"
                        className="recipes-items-outer grid xl:grid-cols-2 gap-[8px] md:gap-[16px] xl:gap-[24px]"
                    >
                        {columnControl[":itemsOrder"].map((columnKey: string) =>
                            renderColumn(columnKey)
                        )}
                    </Box>
                </Box>
            </Box>
        </SiteContainer>
    );
}
 
 